import {
    ApiBaseService,
    type ApiServiceConfig,
    type EnhancedFetch,
    type EnhancedFetchOptions,
    type PaginatedFetch,
    type PaginatedFetchOptions,
} from "./ApiBaseService";
import type {Connection} from "~/models/Connection";
import RatecardService from "~/services/third-party/RatecardService";
import SalesForceService from "~/services/third-party/SalesForceService";
import CarerixService from "~/services/third-party/CarerixService";
import OtysService from "./third-party/OtysService";
import VincereService from "~/services/third-party/VincereService";
import WhatsappService from "~/services/third-party/WhatsappService";
import FacebookService from "~/services/third-party/FacebookService";
import GoogleService from "~/services/third-party/GoogleService";
import ChatgptService from "~/services/third-party/ChatgptService";
import type {ApiResponse} from "~/types/ApiResponse";
import type {LengthAwarePaginator} from "~/types/LengthAwarePaginator";
import type {FetchOptions} from "ofetch";

export interface UnsplashResult {
    id: string,
    slug: string,
    description: string,
    alt_description: string,
    breadcrumbs: {
        slug: string,
        title: string,
        index: number,
        type: string
    }[],
    urls: {
        raw: string,
        full: string,
        regular: string,
        small: string,
        thumb: string,
        small_s3: string
    },
    links: {
        html: string
    },
    user: {
        id: string,
        name: string,
        bio: string,
        links: {
            html: string
        },
        profile_image: string
    },
    tags: {
        type: string,
        title: string
    }[]
}

type UnsplashSearchOptions = {
    search: string
    page?: number
    per_page?: number
    orientation?: "landscape" | "portrait" | "squarish"
    collections?: string
    order_by?: 'relevant' | 'latest'
    color?:
        | 'black_and_white'
        | 'black'
        | 'white'
        | 'yellow'
        | 'orange'
        | 'red'
        | 'purple'
        | 'magenta'
        | 'green'
        | 'teal'
        | 'blue'
    content_filter?: 'low' | 'high'
}

export default class ThirdPartyService extends ApiBaseService {
    public ratecard: RatecardService;

    constructor(config: ApiServiceConfig) {
        super(config);

        this.ratecard = new RatecardService(config);
    }

    public salesforce(uuid: string) {
        return new SalesForceService(uuid, this.getConfig());
    }

    public carerix(uuid: string) {
        return new CarerixService(uuid, this.getConfig());
    }

    public otys(uuid: string) {
        return new OtysService(uuid, this.getConfig());
    }

    public vincere(uuid: string) {
        return new VincereService(uuid, this.getConfig());
    }

    public whatsapp(uuid: string) {
        return new WhatsappService(uuid, this.getConfig());
    }

    public facebook(uuid: string) {
        return new FacebookService(uuid, this.getConfig());
    }

    public google(uuid: string) {
        return new GoogleService(uuid, this.getConfig());
    }

    public chatgpt(uuid: string) {
        return new ChatgptService(uuid, this.getConfig());
    }

    public list(
        options?: PaginatedFetchOptions<Connection>,
    ): PaginatedFetch<Connection> {
        return this.paginate<Connection>(
            this.route("api.connections.index", "/connections"),
            options,
        );
    }

    public listRequest(
        options?: FetchOptions,
    ): Promise<ApiResponse<Connection[]>> {
        return this.request<ApiResponse<Connection[]>>(
            this.route("api.connections.index", "/connections"),
            options,
        );
    }

    public find(
        uuid: string,
        options?: EnhancedFetchOptions<Connection>,
    ): EnhancedFetch<Connection> {
        return this.enhancedFetch<Connection>(
            this.route("api.automations.show", "/connections/{uuid}", {
                uuid,
            }),
            options,
        );
    }

    public objects() {
    }

    public callback(type: string, params: Record<string, any>) {
        if (type == "facebook_leads") {
            return this.request(
                this.route("socialite.callback", "connections/facebook"),
                {
                    method: "POST",
                    body: params,
                },
            );
        }

        return this.request(
            this.route("socialite.callback", "/auth/{type}/callback", {
                type,
            }),
            {
                method: "GET",
                query: params,
            },
        );
    }

    public searchUnsplash(params: UnsplashSearchOptions): Promise<ApiResponse<LengthAwarePaginator<UnsplashResult>>> {
        return this.request<ApiResponse<LengthAwarePaginator<UnsplashResult>>>(this.route("api.unsplash.search", "/third-party/unsplash/search"), {
            method: "GET",
            query: params,
        });
    }
}
